import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import BrowserAfterInstallBanner from "@/components/BrowserAfterInstallBanner";
import PostCheckoutSurfaceGuard from "@/components/PostCheckoutSurfaceGuard";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RouteAwareLoadingScreen } from "./components/loading/RouteAwareLoadingScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { BootstrapGate } from "./components/BootstrapGate";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { BetaAcknowledgmentRouteGate } from "./components/auth/BetaAcknowledgmentRouteGate";
import { AdminOnlyRoute } from "./components/auth/AdminOnlyRoute";
import { useScrollOptimization } from "./hooks/useScrollOptimization";
import { useRoutePreloader } from "./hooks/useRoutePreloader";
import { useIOSZoomGuard } from "./hooks/useIOSZoomGuard";
import { useLifecycleTelemetry } from "./hooks/useLifecycleTelemetry";
import { usePWAVisibilityRevalidation } from "./hooks/usePWAVisibilityRevalidation";
import { useOfflineQueryPersistence } from "./hooks/useQueryPersistence";

import { initializeGlobalMedicationScheduler } from "./lib/globalMedicationScheduler";
import {
  refillNativeMedicationReminders,
  pruneNativeRemindersAgainstServerDelivery,
  clearNativeMedicationReminders,
} from "./lib/nativeReminderScheduler";
import { initializeOfflineData } from "./lib/dataPrefetcher";
import { supabase } from "./integrations/supabase/client";
import { AriaLiveAnnouncer } from "./components/AriaLiveAnnouncer";
import { IOSInstallBanner } from "./components/iOSInstallBanner";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { PerfOverlayMount } from "./components/diagnostics/PerfOverlayMount";
import { PWAOnboardingPrompt } from "./components/onboarding/PWAOnboardingPrompt";
import { PostInstallConfirmationDialog } from "./components/onboarding/PostInstallConfirmationDialog";
import { NotificationBlockedDialog } from "./components/notifications/NotificationBlockedDialog";
import { PerfRouteObserver } from "@/components/perf/PerfRouteObserver";
import { ConversionPageViewTracker } from "@/components/analytics/ConversionPageViewTracker";
import { MetaPixelTracker } from "@/components/analytics/MetaPixelTracker";
import { PerfProfiler } from "@/components/perf/PerfProfiler";
import { SkipToContent } from "./components/SkipToContent";
import { RouteTransitionOverlay } from "./components/loading/RouteTransitionOverlay";
import { LaunchScreen } from "./components/loading/LaunchScreen";
import { startRefreshWatchdog, stopRefreshWatchdog, didWatchdogFire } from "./utils/pwaRefreshWatchdog";
import { logger } from "./lib/logger";
import { logSwStatus, logPwaMode, instrumentFetchForSwLogging } from "./utils/swStatusLogger";
import { initAnalytics, analytics, identifyUser, resetAnalytics } from "./lib/analytics";

// Critical pages - lazy loaded for smaller initial bundle
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Marketing = lazy(() => import("./pages/Marketing"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Features = lazy(() => import("./pages/Features"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TrialSuccess = lazy(() => import("./pages/TrialSuccess"));


// Lazy load - all secondary pages for optimal bundle splitting
const NotFoundCustom = lazy(() => import("./pages/NotFoundCustom").then(m => ({ default: m.NotFoundCustom })));
// Accessibility Statement page deleted pre-launch (ADA over-promise liability).
// Any inbound /accessibility traffic is redirected to "/" below.
const Medications = lazy(() => import("./pages/Medications"));
const Appointments = lazy(() => import("./pages/Appointments"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Install = lazy(() => import("./pages/Install"));
const History = lazy(() => import("./pages/History"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const CareTeam = lazy(() => import("./pages/CareTeam"));
const Export = lazy(() => import("./pages/Export"));
const Help = lazy(() => import("./pages/Help"));
const Credits = lazy(() => import("./pages/Credits"));
const PressKit = lazy(() => import("./pages/PressKit"));
const PressKitDeSotoCounty = lazy(() => import("./pages/PressKitDeSotoCounty"));

// Legal pages - lazy loaded
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const DataBreachPolicy = lazy(() => import("./pages/DataBreachPolicy"));
const MedicalDisclaimer = lazy(() => import("./pages/MedicalDisclaimer"));
const AIWellnessDisclaimer = lazy(() => import("./pages/AIWellnessDisclaimer"));
const FAQ = lazy(() => import("./pages/FAQ"));
const BetaAcknowledgment = lazy(() => import("./pages/BetaAcknowledgment"));

// Beta/Admin pages - lazy loaded
const BetaFAQ = lazy(() => import("./pages/BetaFAQ"));
const BetaEmailApproval = lazy(() => import("./pages/BetaEmailApproval"));
const AdminAIUsage = lazy(() => import("./pages/AdminAIUsage"));
const AdminControlPanel = lazy(() => import("./pages/AdminControlPanel"));
const AdminBetaPrune = lazy(() => import("./pages/AdminBetaPrune"));
const AdminErrors = lazy(() => import("./pages/AdminErrors"));
const AdminEmailDeliverability = lazy(() => import("./pages/AdminEmailDeliverability"));
const AdminMetrics = lazy(() => import("./pages/AdminMetrics"));
const AdminStripePreflight = lazy(() => import("./pages/AdminStripePreflight"));
const AdminBetaAudit = lazy(() => import("./pages/AdminBetaAudit"));

const SecurityDashboard = lazy(() => import("./pages/SecurityDashboard"));
const NewsletterAdmin = lazy(() => import("./pages/NewsletterAdmin"));
const EmailTemplates = lazy(() => import("./pages/EmailTemplates"));

// Utility pages - lazy loaded
const ReviewDownload = lazy(() => import("./pages/ReviewDownload"));
const SalesDownload = lazy(() => import("./pages/SalesDownload"));
const CropImages = lazy(() => import("./pages/CropImages"));
const EmailCollectionSheet = lazy(() => import("./pages/EmailCollectionSheet"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe"));
const UploadEmailLogo = lazy(() => import("./pages/UploadEmailLogo"));
const GenerateLogoBase64 = lazy(() => import("./pages/GenerateLogoBase64"));

// Components - lazy loaded
const GoogleCalendarCallback = lazy(() => import("./components/auth/GoogleCalendarCallback").then(m => ({ default: m.GoogleCalendarCallback })));
const GoogleCalendarDiagnostics = lazy(() => import("./pages/GoogleCalendarDiagnostics"));
const PackageSelection = lazy(() => import("./components/premium/PackageSelection").then(m => ({ default: m.PackageSelection })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
      // Optimize network waterfall
      networkMode: 'online',
    },
    mutations: {
      // Faster error recovery
      retry: 1,
    },
  },
});

// Scroll optimization and route preloading wrapper
const AppContent = () => {
  useScrollOptimization();

  // PostHog: init once on mount, wire auth state to identify/reset,
  // and fire a session_started event for retention math.
  useEffect(() => {
    initAnalytics();
    const isPwa =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      // @ts-expect-error legacy iOS
      window.navigator.standalone === true;
    analytics.sessionStarted({ pwa: !!isPwa, offline: !navigator.onLine });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        identifyUser(session.user.id, { email: session.user.email ?? undefined });
      } else if (event === 'SIGNED_OUT') {
        resetAnalytics();
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  
  // Preload common routes after initial render - defer to idle time
  useRoutePreloader();

  // iOS pinch-zoom guard: lock viewport in standalone PWA + auto-reset stuck
  // zoom on route change (browser tab). Addresses beta feedback that the top
  // of the page becomes unreachable when iOS persists pinch-zoom across nav.
  useIOSZoomGuard();

  // Lifecycle telemetry: capture pagehide / visibilitychange / errors so we
  // can diagnose tester reports of the app "freezing" or closing itself.
  useLifecycleTelemetry();

  // PWA visibility revalidation (fixes "no repaint until focus" issue)
  usePWAVisibilityRevalidation();
  
  // Hydrate and persist React Query cache to IndexedDB for offline access
  useOfflineQueryPersistence();


  // PWA refresh watchdog: start on mount, stop when app is interactive
  useEffect(() => {
    // DEV-only: Log SW status and PWA mode at startup
    if (import.meta.env.DEV) {
      logSwStatus();
      logPwaMode();
      instrumentFetchForSwLogging();
    }

    // Start watchdog on initial load
    startRefreshWatchdog();

    // Stop watchdog when DOM is interactive (React has rendered)
    const stopOnInteractive = () => {
      stopRefreshWatchdog();
      
      // Dev logging
      if (import.meta.env.DEV && didWatchdogFire()) {
        logger.warn('[AppContent] Refresh watchdog fired this session');
      }
    };

    // Use RAF to ensure we're past initial render
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(stopOnInteractive);
    });

    return () => cancelAnimationFrame(id);
  }, []);
  
  // Initialize medication reminder scheduler and offline data after app is interactive
  useEffect(() => {
    // Use requestIdleCallback to defer non-critical initialization
    const scheduleInit = (callback: () => void) => {
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number })
          .requestIdleCallback(callback, { timeout: 5000 });
      } else {
        setTimeout(callback, 1000);
      }
    };

    // Schedule medication reminder and offline data initialization during idle time
    scheduleInit(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Initialize offline data caching (prefetches critical data to IndexedDB)
        await initializeOfflineData();
        // Initialize medication reminders
        await initializeGlobalMedicationScheduler();
        // Native offline backup: schedule a 7-day window of OS-level reminders
        // (no-op on web; safe to call always).
        await refillNativeMedicationReminders();
      }
    });

    // Re-schedule when user logs in
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Small delay to let the UI settle after login
        setTimeout(async () => {
          await initializeOfflineData();
          await initializeGlobalMedicationScheduler();
          await refillNativeMedicationReminders();
        }, 500);
      }
      if (event === 'SIGNED_OUT') {
        void clearNativeMedicationReminders();
      }
    });

    // When the app comes back to the foreground, prune any native reminders
    // whose occurrenceId was already delivered by server push while we were away.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void pruneNativeRemindersAgainstServerDelivery();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Refill the native rolling-window whenever the user's prescriptions or
    // profile (timezone / travel state) change. Debounced to coalesce bursts.
    let refillTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefill = () => {
      if (refillTimer) clearTimeout(refillTimer);
      refillTimer = setTimeout(() => {
        void refillNativeMedicationReminders();
      }, 1500);
    };
    let prescriptionsChannel: ReturnType<typeof supabase.channel> | null = null;
    let profileChannel: ReturnType<typeof supabase.channel> | null = null;
    void supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      prescriptionsChannel = supabase
        .channel(`native-reminders-rx-${user.id}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'prescriptions', filter: `user_id=eq.${user.id}` },
          scheduleRefill,
        )
        .subscribe();
      profileChannel = supabase
        .channel(`native-reminders-profile-${user.id}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
          scheduleRefill,
        )
        .subscribe();
    });

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      if (refillTimer) clearTimeout(refillTimer);
      if (prescriptionsChannel) supabase.removeChannel(prescriptionsChannel);
      if (profileChannel) supabase.removeChannel(profileChannel);
    };
  }, []);
  
  return null;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AriaLiveAnnouncer>
        <TooltipProvider>
          <SkipToContent />
          <Toaster />
          <Sonner />
          <OfflineIndicator />
          <PerfOverlayMount />
          <NotificationBlockedDialog />
        <BrowserRouter>
            <AuthProvider>
              <IOSInstallBanner />
              <PWAOnboardingPrompt />
              <PostInstallConfirmationDialog />
              <BrowserAfterInstallBanner />
              <PostCheckoutSurfaceGuard />
              {/* BootstrapGate ensures ONLY LaunchScreen renders until auth is ready */}
              <BootstrapGate>
                <AppContent />
                <RouteTransitionOverlay />
                <PerfRouteObserver />
                <ConversionPageViewTracker />
                <MetaPixelTracker />
                <BetaAcknowledgmentRouteGate>
                <ChunkErrorBoundary>
                  <Suspense fallback={<LaunchScreen />}>
                    <Routes>
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/marketing" element={<Marketing />} />

                      {/* Public routes */}
                      <Route path="/" element={<Index />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/features" element={<Features />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/data-breach-policy" element={<DataBreachPolicy />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/medical-disclaimer" element={<MedicalDisclaimer />} />
                      <Route path="/ai-wellness-disclaimer" element={<AIWellnessDisclaimer />} />
                      {/* Accessibility Statement removed pre-launch (ADA over-promise liability). Redirect any cached/inbound link to home. */}
                      <Route path="/accessibility" element={<Navigate to="/" replace />} />
                      <Route path="/beta-faq" element={<AdminOnlyRoute><BetaFAQ /></AdminOnlyRoute>} />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
                      <Route path="/credits" element={<Credits />} />
                      <Route path="/press-kit" element={<PressKit />} />
                      <Route path="/press-kit/desoto-county" element={<PressKitDeSotoCounty />} />
                      {/* Beta acknowledgment — hard-gated route; uses its own auth check, no ProtectedRoute wrapper to avoid gate loop */}
                      <Route path="/beta-acknowledgment" element={<BetaAcknowledgment />} />
                      
                      {/* Legacy redirects for removed pages */}
                      <Route path="/beta" element={<Navigate to="/auth" replace />} />
                      <Route path="/beta-signup" element={<Navigate to="/auth" replace />} />
                      <Route path="/lander" element={<Navigate to="/" replace />} />
                      <Route path="/welcome" element={<Navigate to="/" replace />} />
                      {/* Audit-flagged 404s: route to the relevant marketing sections */}
                      <Route path="/about" element={<Navigate to="/marketing#founder" replace />} />
                      <Route path="/customers" element={<Navigate to="/marketing#testimonials" replace />} />
                      {/* Removed translated routes — redirect to English canonical so Google consolidates */}
                      <Route path="/privacy-es" element={<Navigate to="/privacy" replace />} />
                      <Route path="/privacy-fr" element={<Navigate to="/privacy" replace />} />
                      <Route path="/privacy-pt" element={<Navigate to="/privacy" replace />} />
                      <Route path="/terms-es" element={<Navigate to="/terms" replace />} />
                      <Route path="/terms-fr" element={<Navigate to="/terms" replace />} />
                      <Route path="/terms-pt" element={<Navigate to="/terms" replace />} />
                      {/* Common URL aliases (Google OAuth consent screen, external links) */}
                      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                      <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
                      <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />

                      {/* Protected app routes */}
                      {/* Post-checkout confirmation (no wellness data; Meta conversion route) */}
                      <Route path="/trial-success" element={<ProtectedRoute><TrialSuccess /></ProtectedRoute>} />
                      <Route
                        path="/dashboard"
                        element={
                          // E2E stability: guaranteed marker for /dashboard route
                          <div data-testid="dashboard-root">
                            <ProtectedRoute>
                              <PerfProfiler id="route:/dashboard">
                                <Dashboard />
                              </PerfProfiler>
                            </ProtectedRoute>
                          </div>
                        }
                      />
                      <Route path="/select-plan" element={<ProtectedRoute><PackageSelection /></ProtectedRoute>} />
                      <Route path="/medications" element={<ProtectedRoute><Medications /></ProtectedRoute>} />
                      <Route path="/appointments" element={<Navigate to="/dashboard?tab=appointments" replace />} />
                      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                      <Route path="/care-team" element={<ProtectedRoute><CareTeam /></ProtectedRoute>} />
                      <Route path="/export" element={<ProtectedRoute><Export /></ProtectedRoute>} />
                      {/* /help is intentionally public so its canonical → /faq is visible to crawlers.
                          Without this, Googlebot gets redirected to /auth and indexes Auth's noindex tag. */}
                      <Route path="/help" element={<Help />} />
                      <Route path="/install" element={<ProtectedRoute><Install /></ProtectedRoute>} />
                      <Route path="/google-calendar-callback" element={<ProtectedRoute><GoogleCalendarCallback /></ProtectedRoute>} />
                      <Route path="/google-calendar-diagnostics" element={<ProtectedRoute><GoogleCalendarDiagnostics /></ProtectedRoute>} />
                      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
                      <Route path="/review-download" element={<AdminOnlyRoute><ReviewDownload /></AdminOnlyRoute>} />
                      <Route path="/sales-download" element={<ProtectedRoute><SalesDownload /></ProtectedRoute>} />
                      <Route path="/crop-images" element={<ProtectedRoute><CropImages /></ProtectedRoute>} />
                      <Route path="/email-templates" element={<AdminOnlyRoute><EmailTemplates /></AdminOnlyRoute>} />
                      <Route path="/email-collection" element={<ProtectedRoute><EmailCollectionSheet /></ProtectedRoute>} />
                      <Route path="/admin-ai-usage" element={<AdminOnlyRoute><AdminAIUsage /></AdminOnlyRoute>} />
                      <Route path="/admin" element={<AdminOnlyRoute><AdminControlPanel /></AdminOnlyRoute>} />
                      {/* /admin/analytics merged into Metrics Dashboard "Funnel by source" tab */}
                      <Route path="/admin/analytics" element={<Navigate to="/admin/metrics" replace />} />
                      <Route path="/admin/beta-prune" element={<AdminOnlyRoute><AdminBetaPrune /></AdminOnlyRoute>} />
                      <Route path="/admin/beta-audit" element={<AdminOnlyRoute><AdminBetaAudit /></AdminOnlyRoute>} />
                      <Route path="/admin/errors" element={<AdminOnlyRoute><AdminErrors /></AdminOnlyRoute>} />
                      <Route path="/admin/email-deliverability" element={<AdminOnlyRoute><AdminEmailDeliverability /></AdminOnlyRoute>} />
                      <Route path="/admin/metrics" element={<AdminOnlyRoute><AdminMetrics /></AdminOnlyRoute>} />
                      <Route path="/admin/stripe-preflight" element={<AdminOnlyRoute><AdminStripePreflight /></AdminOnlyRoute>} />
                      {/* /admin/lifetime-seats merged into Metrics Dashboard top card */}
                      <Route path="/admin/lifetime-seats" element={<Navigate to="/admin/metrics" replace />} />
                      <Route path="/newsletter-admin" element={<AdminOnlyRoute><NewsletterAdmin /></AdminOnlyRoute>} />
                      <Route path="/beta-email-approval" element={<AdminOnlyRoute><BetaEmailApproval /></AdminOnlyRoute>} />
                      <Route path="/security-dashboard" element={<AdminOnlyRoute><SecurityDashboard /></AdminOnlyRoute>} />
                      <Route path="/upload-email-logo" element={<AdminOnlyRoute><UploadEmailLogo /></AdminOnlyRoute>} />
                      <Route path="/generate-logo-base64" element={<AdminOnlyRoute><GenerateLogoBase64 /></AdminOnlyRoute>} />

                      <Route path="*" element={<NotFoundCustom />} />
                    </Routes>
                  </Suspense>
                </ChunkErrorBoundary>
                </BetaAcknowledgmentRouteGate>
              </BootstrapGate>
            </AuthProvider>
        </BrowserRouter>
        </TooltipProvider>
      </AriaLiveAnnouncer>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;